import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Row, Col } from "react-bootstrap";

import {
  buttonNameConstants,
  EMPTY_STRING,
  inventoryConstants,
} from "../../../constants";
import { inventoryActions } from "../../../redux/actions";
import { isSpinnerEnabled } from "../../utility";
import CustomBodyModal from "../modal/CustomBodyModal";
import { DeleteIcon } from "../../../assets/icons/iconsProvider";

const ALLOWED_TYPES = ["image/png", "image/jpeg"];

const ProductImagesModal = ({
  size = "md",
  title = "Product Images",
  isOpen,
  toggleModal,
  mainImage, // real main image URL (pass undefined/"" if none is set)
  spinnerArray,
  productDataObject,
  setProductDataObject,
  imgUrlExtras = [],
  handleSubmit = () => {}, // (finalUrls: string[]) => void
  className = EMPTY_STRING,
  uploadProductImage,
}) => {
  const fileInputRef = useRef(null);
  const draftRef = useRef([]);
  // draft item: { id, url, file, checked, isNew }
  const [draftImages, setDraftImages] = useState([]);
  const [mainId, setMainId] = useState(null);
  const isSubmitting = isSpinnerEnabled(
    spinnerArray,
    inventoryConstants.UPLOAD_IMAGE
  );

  // If no explicit main is chosen (or it was deleted), the first image is main
  const effectiveMainId = draftImages.some((i) => i.id === mainId)
    ? mainId
    : draftImages[0]?.id ?? null;

  const updateDraft = (updater) => {
    setDraftImages((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      draftRef.current = next;
      return next;
    });
  };

  const revokePreviews = (items) => {
    items.forEach((i) => i.isNew && URL.revokeObjectURL(i.url));
  };

  useEffect(() => {
    if (isOpen) {
      // main image first, then extras (deduped)
      const urls = [...new Set([mainImage, ...imgUrlExtras].filter(Boolean))];
      updateDraft(
        urls.map((url, index) => ({
          id: `saved-${index}-${url}`,
          url,
          file: null,
          checked: true, // kept so the upload action still treats it as selected
          isNew: false,
        }))
      );
      setMainId(null); // defaults to first item (the current main)
    } else {
      revokePreviews(draftRef.current);
      updateDraft([]);
      setMainId(null);
    }
    // eslint-disable-next-line
  }, [isOpen]);

  useEffect(() => () => revokePreviews(draftRef.current), []);

  const handleSetMain = (id) => {
    if (isSubmitting) return;
    setMainId(id);
  };

  const handleRemove = (e, id) => {
    e.stopPropagation();
    if (isSubmitting) return;
    const target = draftRef.current.find((i) => i.id === id);
    if (target) revokePreviews([target]);
    updateDraft((prev) => prev.filter((img) => img.id !== id));
  };

  // Local only: nothing is uploaded until Submit
  const handleAddImage = (e) => {
    const files = Array.from(e.target.files || []).filter((f) =>
      ALLOWED_TYPES.includes(f.type)
    );
    if (files.length) {
      updateDraft((prev) => [
        ...prev,
        ...files.map((file) => ({
          id: `new-${file.name}-${file.size}-${
            file.lastModified
          }-${Math.random()}`,
          url: URL.createObjectURL(file),
          file,
          checked: true,
          isNew: true,
        })),
      ]);
    }
    e.target.value = "";
  };

  const onSubmit = async () => {
    if (isSubmitting) return;

    // Put the main image first so finalUrls[0] is the main image
    const current = draftRef.current;
    const main = current.find((i) => i.id === effectiveMainId);
    const ordered = main
      ? [main, ...current.filter((i) => i.id !== main.id)]
      : current;

    await uploadProductImage(
      ordered,
      undefined,
      undefined,
      undefined,
      ({ finalUrls }) => {
        const [newMain, ...extras] = finalUrls || [];
        handleSubmit(finalUrls);
        toggleModal();
        setProductDataObject({
          ...productDataObject,
          imgUrl: newMain || EMPTY_STRING,
          imgUrlExtras: extras,
        });
      }
    );
  };

  return (
    <CustomBodyModal
      size={size}
      show={isOpen}
      title={title}
      closeButton={!isSubmitting}
      onHide={isSubmitting ? () => {} : toggleModal}
      className={`nested-modal-z-index ${className}`}
      backdropClassName="nested-modal-z-index"
      showActionButtons
      onSubmit={onSubmit}
      onCancel={toggleModal}
      isLoading={isSubmitting}
      submitButtonLabel={buttonNameConstants.SUBMIT}
    >
      <Row className="m-0 p-0">
        <Col md={12} className="p-0">
          <div className="product-images-grid">
            {draftImages.map((img, index) => {
              const isMain = img.id === effectiveMainId;
              return (
                <div
                  className={`product-image-tile ${isMain ? "main" : ""}`}
                  key={img.id}
                  onClick={() => handleSetMain(img.id)}
                  title={isMain ? "Main image" : "Click to set as main"}
                >
                  <img src={img.url} alt={`Product ${index + 1}`} />

                  <button
                    type="button"
                    className="product-image-delete"
                    onClick={(e) => handleRemove(e, img.id)}
                    disabled={isSubmitting}
                    title="Remove image"
                    aria-label="Remove image"
                  >
                    <DeleteIcon />
                  </button>

                  {isMain && <span className="product-image-label">Main</span>}
                </div>
              );
            })}

            <button
              type="button"
              className="product-image-tile add-tile"
              onClick={() => fileInputRef.current?.click()}
              disabled={isSubmitting}
              title="Add image"
            >
              +
            </button>
          </div>

          <input
            type="file"
            className="d-none"
            accept=".png, .jpg, .jpeg"
            multiple
            ref={fileInputRef}
            onChange={handleAddImage}
          />
        </Col>
      </Row>
    </CustomBodyModal>
  );
};

const mapStateToProps = (state) => ({
  currentStore: state.store.currentStore,
  spinnerArray: state.system.spinnerArray,
});

const mapDispatchToProps = (dispatch) => ({
  uploadProductImage: (...args) =>
    dispatch(inventoryActions.uploadProductImage(...args)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductImagesModal);
